// src/layouts/Pages/PageHeader/FilterButton.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { FilterOutlined } from '@ant-design/icons';
import { Button, Popover, Tooltip } from 'antd';
import React, { useCallback, useState } from 'react';

import { useModuleContext } from 'components/core/ModuleProvider';
import useZustand from 'hooks/useZustand';

import './FilterButton.less';
import { useModuleStore } from 'stores/module';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
export const FilterButton = ({
  actions = [],
  actionContext,
  label,
  tooltip,
  icon,
  type,
  disabled,
  onClick,
  popoverContent,
  hasActiveFilter,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedListSettings = useZustand('module', 'listSettings');
  const { handleShowFilterDrawer, hasDrawerFilter } = useModuleContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled;
  const showFilterButton = actions.includes('filter');

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleClosePopover = useCallback(() => setPopoverOpen(false), []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClick = () => {
    if (popoverContent) return; // Popover handles click
    if (typeof onClick === 'function') {
      onClick({
        navigate: actionContext?.navigate,
        listSettings: useModuleStore.getState().listSettings,
        page: useModuleStore.getState().currentPage,
        actionContext,
      });
      return;
    }
    handleShowFilterDrawer?.();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const buttonIcon = icon || <FilterOutlined />;
  const buttonType = hasActiveFilter ? 'primary' : type;
  const title = translateX(tooltip || label || 'filter');

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!showFilterButton) return null;

  const button = (
    <Tooltip title={popoverContent ? '' : title} placement="top">
      <Button
        size="middle"
        type={buttonType}
        className="filter-button"
        style={
          isDisabled
            ? filterButtonDisabledStyle(isLabelProvided(label))
            : filterButtonStyle(isLabelProvided(label))
        }
        disabled={isDisabled}
        danger={hasDrawerFilter}
        icon={buttonIcon}
        onClick={handleClick}
      >
        {isLabelProvided(label) ? translateX(label) : null}
      </Button>
    </Tooltip>
  );

  if (popoverContent) {
    const enhancedContent = React.isValidElement(popoverContent)
      ? React.cloneElement(popoverContent, {
          onRequestClose: handleClosePopover,
          onOpenDrawer: handleShowFilterDrawer,
          isOpen: popoverOpen,
        })
      : popoverContent;

    return (
      <Popover
        content={enhancedContent}
        trigger="click"
        placement="bottom"
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        overlayClassName="filter-button-popover"
      >
        {button}
      </Popover>
    );
  }

  return button;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FilterButton;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function isLabelProvided(label) {
  return !!label && typeof label === 'string';
}

function filterButtonStyle(hasLabel) {
  return { width: hasLabel ? undefined : 40, height: 40 };
}

function filterButtonDisabledStyle(hasLabel) {
  return { ...filterButtonStyle(hasLabel), backgroundColor: 'var(--ant-color-bg-container)' };
}
